import React from 'react';
import { Button, Checkbox, Descriptions, Space, Tag, Typography, Upload } from 'antd';
import { CloudServerOutlined, DownloadOutlined, LinkOutlined, SettingOutlined, UploadOutlined } from '@ant-design/icons';
import type { SettingsController } from '../../hooks/useSettings';
import { SettingsCard } from './parts';

const { Text } = Typography;

const DENSE_LABEL = { fontSize: 12, padding: '4px 8px', width: 110 };
const DENSE_CONTENT = { fontSize: 12, padding: '4px 8px' };

export const BackupRestoreCard: React.FC<{ s: SettingsController }> = ({ s }) => (
  <SettingsCard
    title="Backup & Restore"
    icon={<DownloadOutlined />}
    extra={
      <Space size={8}>
        <Button size="small" icon={<DownloadOutlined />} onClick={s.handleExport} loading={s.exporting}>
          Export
        </Button>
        <Upload accept=".json" showUploadList={false} beforeUpload={s.handleFileRead}>
          <Button size="small" icon={<UploadOutlined />}>Import file</Button>
        </Upload>
      </Space>
    }
  >
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
      <Text style={{ fontSize: 13 }}>Export</Text>
      <Text type="secondary" style={{ fontSize: 12 }}>JSON backup of the sections ticked here</Text>
    </div>
    <Checkbox.Group
      value={s.exportSections}
      onChange={(vals) => s.setExportSections(vals as string[])}
      options={[
        { label: 'App Settings', value: 'settings' },
        { label: 'Server List', value: 'servers' },
        { label: 'Webhooks', value: 'webhooks' },
      ]}
      style={{ fontSize: 12, marginBottom: 10 }}
    />

    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
      <Text style={{ fontSize: 13 }}>Import</Text>
      <Text type="secondary" style={{ fontSize: 12 }}>Restore from a JSON backup file</Text>
    </div>

    {s.importData && (
      <div style={{ marginTop: 8 }}>
        <Descriptions column={1} size="small" bordered labelStyle={DENSE_LABEL} contentStyle={DENSE_CONTENT}>
          <Descriptions.Item label="File">{s.importFileName}</Descriptions.Item>
          <Descriptions.Item label="Version">{s.importData.version || '—'}</Descriptions.Item>
          <Descriptions.Item label="Exported At">
            {s.importData.exportedAt ? new Date(s.importData.exportedAt).toLocaleString() : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Contents">
            <Space size={4} wrap>
              {s.importData.settings && <Tag icon={<SettingOutlined />} color="blue">Settings</Tag>}
              {s.importData.servers?.length > 0 && (
                <Tag icon={<CloudServerOutlined />} color="green">
                  {s.importData.servers.length} servers
                </Tag>
              )}
              {s.importData.webhooks?.length > 0 && (
                <Tag icon={<LinkOutlined />} color="purple">
                  {s.importData.webhooks.length} webhooks
                </Tag>
              )}
            </Space>
          </Descriptions.Item>
        </Descriptions>

        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Select what to import:</Text>
        </div>
        <Checkbox.Group
          value={s.importSections}
          onChange={(vals) => s.setImportSections(vals as string[])}
          options={[
            ...(s.importData.settings ? [{ label: 'App Settings', value: 'settings' }] : []),
            ...(s.importData.servers?.length > 0
              ? [{ label: `Server List (${s.importData.servers.length})`, value: 'servers' }] : []),
            ...(s.importData.webhooks?.length > 0
              ? [{ label: `Webhooks (${s.importData.webhooks.length})`, value: 'webhooks' }] : []),
          ]}
          style={{ fontSize: 12, marginBottom: 8 }}
        />

        <Space size={8}>
          <Button size="small" type="primary" onClick={s.handleImport} loading={s.importing}
            disabled={s.importSections.length === 0}>
            Import
          </Button>
          <Button size="small" onClick={s.clearImport}>Cancel</Button>
        </Space>
      </div>
    )}
  </SettingsCard>
);

export default BackupRestoreCard;
